<template>
  <v-container fluid class="dashboard-container container-full-bg-gray">
    <!-- Loading Spinner -->
    <v-overlay :value="screen_data.isLoading" absolute>
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Main Dashboard Card -->
    <v-card class="dashboard-kpis-fallback-card">
      <v-card-title class="pb-0">
        {{ dashboardTitle }}
        <v-spacer></v-spacer>

        <!-- Filtros Search Bar - Reemplaza los selectores anteriores -->
        <app-search-bar
          v-if="!screen_data.isLoading && hasDashboardData"
          :filters="dashboardFilters"
          :initial_filters="activeFiltersForSearchBar"
          @updateObject="updateDashboardFilter"
          @resetAllFilters="resetDashboardFilters"
          :show_delete_filters="true"
          class="mr-2"
        />

        <v-btn
          icon
          color="primary"
          class="ml-2"
          @click="create_dashboard_op"
          title="Actualizar datos"
          v-if="hasDashboardData"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Warning message -->
      <v-alert
        v-if="!screen_data.isLoading && hasDashboardData"
        dense
        text
        color="amber darken-2"
        class="warning-text mx-0 mt-0"
        icon="mdi-information-outline"
      >
        La información mostrada en esta página tiene carácter meramente informativo, sin que en ningún caso puedan tomarse como datos definitivos.
      </v-alert>

      <!-- Tabs Section -->
      <v-tabs v-if="!screen_data.isLoading && hasDashboardData" v-model="activeTab" class="dashboard-tabs">
        <v-tab>General</v-tab>
        <v-tab>P&G</v-tab>
      </v-tabs>

      <v-tabs-items v-if="!screen_data.isLoading && hasDashboardData" v-model="activeTab">
        <!-- General Tab -->
        <v-tab-item>
          <dashboard-general
            :dashboard-data="filteredDashboardDataComputed"
            :selected-fiscal-year="effectiveFiscalYear"
            :selected-fiscal-years="selectedExercises"
            :selected-month="effectiveMonth"
            :is-loading="screen_data.isLoading"
          />
        </v-tab-item>

        <!-- P&G Tab -->
        <v-tab-item>
          <dashboard-py-g
            :selected-fiscal-year="effectiveFiscalYear"
            :selected-fiscal-years="selectedExercises"
            :pyg-grupos="screen_data.pygGrupos"
            :pyg-data="filteredPygDataComputed"
            :is-loading="screen_data.isLoading"
          />
        </v-tab-item>
      </v-tabs-items>

      <!-- Empty State - No Dashboard Data -->
      <v-card-text v-if="!screen_data.isLoading && !hasDashboardData">
        <div class="centered-content">
          <v-icon large class="primary--text">mdi-view-dashboard</v-icon>
          <div class="primary--text font-30 pb-7">
            Dashboard KPIs
          </div>
          <div class="font-weight-light pb-3">
            Descubre la facilidad de visualizar<br>
            tus indicadores de negocio con nosotros.
          </div>
          <div class="pb-7 flex flex-col">
            <small>Carga tus datos para comenzar a visualizar tus KPIs.</small>
          </div>
          <v-btn
              rounded
              color="primary"
              :loading="buttonLoading"
              @click="create_dashboard_op"
          >
            Cargar tus datos
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { LoadDashboard } from '@/modules/dashboard/application/LoadDashboard';
import { LoadPyGData } from '@/modules/dashboard/application/LoadPyGData';
import { ApiDashboardRepository } from '@/modules/dashboard/infra/ApiDashboardRepository';
import { MemoryDashboardRepository } from '@/modules/dashboard/infra/MemoryDashboardRepository';
import {client2, query_dispatcher_2} from "@/api/client";
import VueApexCharts from 'vue-apexcharts';
import { mapGetters, mapState, mapActions } from "vuex";
import { mapFields } from 'vuex-map-fields';
import {vuexPrefix} from "@/store/vuex_utils";
import DashboardPyG from './kpis/DashboardPyG.vue';
import DashboardGeneral from './kpis/DashboardGeneral.vue';
import AppSearchBar from '@/components/layout/AppSearchBar.vue';

export default {
  components: {
    apexchart: VueApexCharts,
    DashboardPyG,
    AppSearchBar,
    DashboardGeneral
  },

  data() {
    return {
      activeTab: 0,  // Controls which tab is active (0 = Dashboard, 1 = P&G)
      screen_data: {
        message: '',
        isLoading: false,
        dashboardData: null,
        pygGrupos: [],
        pygData: []
      },
      chartView: 'todos',
      search: '',
      selectedFiscalYear: null,    // Global selected fiscal year for all charts
      selectedMonth: 'todos',    // Selected month/quarter for filtering Top charts
      invoiceHeaders: [
        { text: 'Fecha', value: 'fecha' },
        { text: 'ID', value: 'id' },
        { text: 'Tipo', value: 'tipo' },
        { text: 'Tercero', value: 'origen' },
        { text: 'Importe', value: 'importe' },
        { text: 'Estado', value: 'estado' }
      ],
      retryCount: 0,
      maxRetries: 5,
      retryInterval: 1000, // 1 second
      dashboardOperationState: 'done', // States: 'pending', 'done', 'error'
      dashboardOperationId: null,
      intervalDashboard: null,
      buttonLoading: false // Specific loading state for the "CARGAR TUS DATOS" button
    };
  },

  computed: {
    ...mapState(['currentCompany', 'session_information']),
    ...mapFields('me',vuexPrefix('user_data_',['id', ],'userData.')),
    ...mapGetters({
        me: 'me/me',
        userData: 'me/userData',
        availableDocs: 'me/availableDocs',
        is_mobile: 'is_mobile',
        permission: 'permissions/permission',
        got_feature: 'got_feature',
    }),
    ...mapGetters('dashboard', ['dashboardFilters']),
    
    // Data availability checks for conditional rendering
    hasTotalKpisData() {
      // Check if dashboardData and monthlyKpis exist
      if (!this.screen_data.dashboardData ||
          !this.screen_data.dashboardData.monthlyKpis ||
          !this.screen_data.dashboardData.monthlyKpis.length) {
        return false;
      }

      // If we have monthlyKpis data, we can calculate totalKpis
      // The actual calculation is done in filteredTotalKpis computed property
      return true;
    },

    
    // Filtered totalKpis based on selected fiscal year and month
    filteredTotalKpis() {
      // First, check if dashboardData and monthlyKpis exist
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.monthlyKpis || this.screen_data.dashboardData.monthlyKpis.length === 0) {
        return {
          ejercicio: this.selectedFiscalYear || new Date().getFullYear(),
          ingresos_totales: 0,
          gastos_totales: 0,
          beneficio_perdida: 0,
          margen_neto: 0
        };
      }
      
      // Filter monthly data based on selected fiscal year
      let filteredMonthlyData = this.screen_data.dashboardData.monthlyKpis.filter(item => {
        if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
          return false;
        }
        
        try {
          const [, year] = item.mes_ejercicio.split('/');
          const yearNum = parseInt(year);
          return this.selectedFiscalYear === null || yearNum === this.selectedFiscalYear;
        } catch (error) {
          console.error('Error parsing mes_ejercicio:', error, item);
          return false;
        }
      });
      
      // Further filter by selected month if not 'todos'
      if (this.selectedMonth !== 'todos') {
        filteredMonthlyData = filteredMonthlyData.filter(item => {
          return this.monthMatchesFilter(item.mes_ejercicio);
        });
      }
      
      // If no data after filtering, return default object
      if (filteredMonthlyData.length === 0) {
        return {
          ejercicio: this.selectedFiscalYear || new Date().getFullYear(),
          ingresos_totales: 0,
          gastos_totales: 0,
          beneficio_perdida: 0,
          margen_neto: 0
        };
      }
      
      // Calculate totals from filtered monthly data
      const ingresos_totales = filteredMonthlyData.reduce((sum, item) => sum + (item.ingresos || 0), 0);
      const gastos_totales = filteredMonthlyData.reduce((sum, item) => sum + (item.gastos || 0), 0);
      const beneficio_perdida = ingresos_totales - gastos_totales;
      
      // Calculate margen_neto (as a percentage)
      let margen_neto = 0;
      if (ingresos_totales > 0) {
        margen_neto = (beneficio_perdida / ingresos_totales) * 100;
      }
      
      return {
        ejercicio: this.selectedFiscalYear || new Date().getFullYear(),
        ingresos_totales,
        gastos_totales,
        beneficio_perdida,
        margen_neto: parseFloat(margen_neto.toFixed(2))
      };
    },
    
    hasMonthlyKpisData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.monthlyKpis && 
             this.screen_data.dashboardData.monthlyKpis.length > 0;
    },
    
    hasExpensesData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.gastosIngresos && 
             this.screen_data.dashboardData.gastosIngresos.filter(item => item.is_gasto).length > 0;
    },
    
    hasIncomeData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.gastosIngresos && 
             this.screen_data.dashboardData.gastosIngresos.filter(item => !item.is_gasto).length > 0;
    },
    
    hasInvoicesData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.ultimasFacturas && 
             this.screen_data.dashboardData.ultimasFacturas.length > 0;
    },
    
    hasClientesProveedoresData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.clientesProveedores && 
             this.screen_data.dashboardData.clientesProveedores.length > 0;
    },
    
    hasClientesProveedoresExpensesData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.clientesProveedores && 
             this.screen_data.dashboardData.clientesProveedores.filter(item => item.is_gasto).length > 0;
    },
    
    hasClientesProveedoresIncomeData() {
      return this.screen_data.dashboardData && 
             this.screen_data.dashboardData.clientesProveedores && 
             this.screen_data.dashboardData.clientesProveedores.filter(item => !item.is_gasto).length > 0;
    },
    
    // Check if any dashboard data exists
    hasDashboardData() {
      return this.hasTotalKpisData || 
             this.hasMonthlyKpisData || 
             this.hasExpensesData || 
             this.hasIncomeData ||
             this.hasClientesProveedoresData;
    },
    
    // Global available fiscal years from all data sources
    availableFiscalYears() {
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.gastosIngresos) {
        return [];
      }
      
      // Extract unique fiscal years from all data sources
      const years = [...new Set(
          this.screen_data.dashboardData.gastosIngresos
              .map(item => {
                if (item.mes_ejercicio && typeof item.mes_ejercicio === 'string') {
                  const parts = item.mes_ejercicio.split('/');
                  return parseInt(parts[1]);
                }
                return null;
              })
              .filter(year => year !== null)
      )];
      
      // Also extract years from monthlyKpis if available
      if (this.screen_data.dashboardData.monthlyKpis && this.screen_data.dashboardData.monthlyKpis.length > 0) {
        const monthlyYears = this.screen_data.dashboardData.monthlyKpis
          .map(item => {
            const [, year] = item.mes_ejercicio.split('/');
            return parseInt(year);
          });
        years.push(...monthlyYears);
      }
      
      // Get unique years and sort in ascending order
      return [...new Set(years)].sort((a, b) => a - b);
    },
    
    // Month/quarter options for the month selector
    monthOptions() {
      // Create an array with "Todos" and quarters
      const options = [
        { text: 'Todos', value: 'todos' },
      ];
      
      // Add individual months
      const months = [
        { text: 'Enero', value: '1' },
        { text: 'Febrero', value: '2' },
        { text: 'Marzo', value: '3' },
        { text: 'Abril', value: '4' },
        { text: 'Mayo', value: '5' },
        { text: 'Junio', value: '6' },
        { text: 'Julio', value: '7' },
        { text: 'Agosto', value: '8' },
        { text: 'Septiembre', value: '9' },
        { text: 'Octubre', value: '10' },
        { text: 'Noviembre', value: '11' },
        { text: 'Diciembre', value: '12' }
      ];
      // Add quarters
      const quarters = [
        { text: 'Trimestre 1', value: 'q1' },
        { text: 'Trimestre 2', value: 'q2' },
        { text: 'Trimestre 3', value: 'q3' },
        { text: 'Trimestre 4', value: 'q4' }
      ];
      
      return [...options, ...months, ...quarters];
    },
    
    
    // Get display text for the selected month/quarter
    selectedMonthText() {
      if (this.selectedMonth === 'todos') {
        return '';
      }
      
      // Find the selected month option
      const option = this.monthOptions.find(opt => opt.value === this.selectedMonth);
      return option ? option.text : '';
    },
    
    // Filtered expenses data based on selected year and month/quarter
    // When 'Todos' or a quarter is selected, this method aggregates expenses by description
    // and returns the top 10 highest expenses
    filteredExpensesData() {
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.gastosIngresos) {
        return [];
      }
      
      // Get all expenses (is_gasto = true)
      const allExpenses = this.screen_data.dashboardData.gastosIngresos.filter(item => item.is_gasto);
      
      // Filter expenses data by selected global fiscal year and month/quarter
      const filteredData = allExpenses.filter(item => {
        // Check fiscal year if selected
        if (this.selectedFiscalYear !== null) {
          // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          const parts = item.mes_ejercicio.split('/');
          if (parts.length !== 2) {
            return false;
          }
          
          const year = parseInt(parts[1]);
          if (isNaN(year) || year !== this.selectedFiscalYear) {
            return false;
          }
        }
        
        // Check month/quarter if selected
        if (this.selectedMonth !== 'todos') {
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          return this.monthMatchesFilter(item.mes_ejercicio);
        }
        
        // If 'todos' is selected for month, include all items that match the year
        return true;
      });
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered income data based on selected year and month/quarter
    // When 'Todos' or a quarter is selected, this method aggregates income by description
    // and returns the top 10 highest income sources
    filteredIncomeData() {
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.gastosIngresos) {
        return [];
      }
      
      // Get all income items (is_gasto = false)
      const allIncome = this.screen_data.dashboardData.gastosIngresos.filter(item => !item.is_gasto);
      
      // Filter income data by selected global fiscal year and month/quarter
      const filteredData = allIncome.filter(item => {
        // Check fiscal year if selected
        if (this.selectedFiscalYear !== null) {
          // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          const parts = item.mes_ejercicio.split('/');
          if (parts.length !== 2) {
            return false;
          }
          
          const year = parseInt(parts[1]);
          if (isNaN(year) || year !== this.selectedFiscalYear) {
            return false;
          }
        }
        
        // Check month/quarter if selected
        if (this.selectedMonth !== 'todos') {
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          return this.monthMatchesFilter(item.mes_ejercicio);
        }
        
        // If 'todos' is selected for month, include all items that match the year
        return true;
      });
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered clientes proveedores expenses data based on selected year and month/quarter
    filteredClientesProveedoresExpensesData() {
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.clientesProveedores) {
        return [];
      }
      
      // Get all expenses (is_gasto = true)
      const allExpenses = this.screen_data.dashboardData.clientesProveedores.filter(item => item.is_gasto);
      
      // Filter expenses data by selected global fiscal year and month/quarter
      const filteredData = allExpenses.filter(item => {
        // Check fiscal year if selected
        if (this.selectedFiscalYear !== null) {
          // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          const parts = item.mes_ejercicio.split('/');
          if (parts.length !== 2) {
            return false;
          }
          
          const year = parseInt(parts[1]);
          if (isNaN(year) || year !== this.selectedFiscalYear) {
            return false;
          }
        }
        
        // Check month/quarter if selected
        if (this.selectedMonth !== 'todos') {
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          return this.monthMatchesFilter(item.mes_ejercicio);
        }
        
        // If 'todos' is selected for month, include all items that match the year
        return true;
      });
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered clientes proveedores income data based on selected year and month/quarter
    filteredClientesProveedoresIncomeData() {
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.clientesProveedores) {
        return [];
      }
      
      // Get all income items (is_gasto = false)
      const allIncome = this.screen_data.dashboardData.clientesProveedores.filter(item => !item.is_gasto);
      
      // Filter income data by selected global fiscal year and month/quarter
      const filteredData = allIncome.filter(item => {
        // Check fiscal year if selected
        if (this.selectedFiscalYear !== null) {
          // Extract year from mes_ejercicio (format: "month/year" or "m/yyyy")
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          const parts = item.mes_ejercicio.split('/');
          if (parts.length !== 2) {
            return false;
          }
          
          const year = parseInt(parts[1]);
          if (isNaN(year) || year !== this.selectedFiscalYear) {
            return false;
          }
        }
        
        // Check month/quarter if selected
        if (this.selectedMonth !== 'todos') {
          if (!item.mes_ejercicio || typeof item.mes_ejercicio !== 'string') {
            return false;
          }
          
          return this.monthMatchesFilter(item.mes_ejercicio);
        }
        
        // If 'todos' is selected for month, include all items that match the year
        return true;
      });
      
      // If 'todos' or a quarter is selected, aggregate the results by description
      if (this.selectedMonth === 'todos' || 
          this.selectedMonth === 'q1' || 
          this.selectedMonth === 'q2' || 
          this.selectedMonth === 'q3' || 
          this.selectedMonth === 'q4') {
        
        // Group by description and sum the totals
        const aggregatedData = [];
        const groupedByDescription = {};
        
        filteredData.forEach(item => {
          if (!groupedByDescription[item.descripcion]) {
            groupedByDescription[item.descripcion] = {
              mes_ejercicio: item.mes_ejercicio, // Keep the first month for reference
              is_gasto: item.is_gasto,
              descripcion: item.descripcion,
              total: 0,
              cuenta: item.cuenta
            };
            aggregatedData.push(groupedByDescription[item.descripcion]);
          }
          
          groupedByDescription[item.descripcion].total += item.total;
        });
        
        // Sort by total in descending order and limit to top 10
        return aggregatedData
          .sort((a, b) => b.total - a.total)
          .slice(0, 10);
      }
      
      // For individual months, just sort and limit to top 10
      return filteredData
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
    },
    
    // Filtered monthly data based on selected global fiscal year
    filteredMonthlyData() {
      if (!this.screen_data.dashboardData || !this.screen_data.dashboardData.monthlyKpis) {
        return [];
      }
      
      // If no fiscal year is selected, return all data
      if (this.selectedFiscalYear === null) {
        return this.screen_data.dashboardData.monthlyKpis;
      }
      
      // Filter monthly data by selected global fiscal year
      return this.screen_data.dashboardData.monthlyKpis.filter(item => {
        const [, year] = item.mes_ejercicio.split('/');
        return parseInt(year) === this.selectedFiscalYear;
      });
    },

    // Colors for charts
    chartColors() {
      const colors = {
        ingresos: '#4CAF50', // Green for Ingresos
        gastos: '#F44336',   // Red for Gastos
        beneficio: '#2196F3' // Blue for Beneficio
      };
      
      // Return colors based on the selected view
      if (this.chartView === 'ingresos') {
        return [colors.ingresos];
      } else if (this.chartView === 'gastos') {
        return [colors.gastos];
      } else if (this.chartView === 'beneficio') {
        return [colors.beneficio];
      } else {
        // 'todos' view - return all colors in the correct order
        return [colors.ingresos, colors.gastos, colors.beneficio];
      }
    },
    
    // Monthly Evolution Chart
    monthlyChartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: {
            show: true
          },
          foreColor: '#333',
          background: 'transparent'
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        xaxis: {
          categories: this.filteredMonthlyData.map(item => {
            const [month, year] = item.mes_ejercicio.split('/');
            return `${this.getMonthName(parseInt(month))} ${year}`;
          }),
          labels: {
            style: {
              colors: '#333'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#333'
            },
            formatter: (value) => {
              return this.formatCurrency(value, false);
            }
          }
        },
        grid: {
          borderColor: '#ddd',
          row: {
            colors: ['#f8f9fa', '#ffffff']
          }
        },
        colors: this.chartColors,
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value) => this.formatCurrency(value)
          }
        },
        legend: {
          position: 'top',
          labels: {
            colors: '#333'
          }
        }
      };
    },

    monthlyChartSeries() {
      if (!this.filteredMonthlyData.length) return [];

      const series = [];

      if (this.chartView === 'ingresos' || this.chartView === 'todos') {
        series.push({
          name: 'Ingresos',
          data: this.filteredMonthlyData.map(item => item.ingresos)
        });
      }

      if (this.chartView === 'gastos' || this.chartView === 'todos') {
        series.push({
          name: 'Gastos',
          data: this.filteredMonthlyData.map(item => item.gastos)
        });
      }

      if (this.chartView === 'beneficio' || this.chartView === 'todos') {
        series.push({
          name: 'Beneficio',
          data: this.filteredMonthlyData.map(item => item.beneficio)
        });
      }

      return series;
    },

    // Expenses Pie Chart
    expensesChartOptions() {
      return {
        chart: {
          type: 'pie',
          foreColor: '#333',
          background: 'transparent'
        },
        labels: this.filteredExpensesData.map(item => item.descripcion),
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value, { dataPointIndex }) => {
              // Get the corresponding item from filteredExpensesData
              const item = this.filteredExpensesData[dataPointIndex];
              if (item) {
                return `<div style="padding: 5px;">
                  <div style="font-weight: bold; margin-bottom: 5px;">${item.descripcion}</div>
                  <div style="margin-bottom: 3px;">Importe: ${this.formatCurrency(item.total)}</div>
                  <div>Cuenta: ${item.cuenta}</div>
                </div>`;
              }
              return this.formatCurrency(value);
            }
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        dataLabels: {
          style: {
            colors: ['#fff']
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    expensesChartSeries() {
      return this.filteredExpensesData.map(item => item.total);
    },

    // Income Bar Chart
    incomeChartOptions() {
      return {
        chart: {
          type: 'bar',
          foreColor: '#333',
          background: 'transparent'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatCurrency(val, false),
          offsetX: 30,
          style: {
            colors: ['#333']
          }
        },
        xaxis: {
          categories: this.filteredIncomeData.map(item => item.descripcion),
          labels: {
            style: {
              colors: '#333'
            },
            formatter: (value) => this.formatCurrency(value, false)
          }
        },
        grid: {
          borderColor: '#ddd',
          row: {
            colors: ['#f8f9fa', '#ffffff']
          }
        },
        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B'],
        tooltip: {
          theme: 'light',
          custom: ({ dataPointIndex }) => {
            const item = this.filteredIncomeData[dataPointIndex];
            return `
              <div class="apexcharts-tooltip-box">
                <div class="apexcharts-tooltip-title">${item.descripcion}</div>
                <div>Importe: ${this.formatCurrency(item.total)}</div>
                <div>Cuenta: ${item.cuenta}</div>
              </div>
            `;
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    incomeChartSeries() {
      const data = this.filteredIncomeData.map(item => item.total);
      return [{
        name: 'Ingresos',
        data: data
      }];
    },

    // Clientes Proveedores Expenses Pie Chart
    clientesProveedoresExpensesChartOptions() {
      return {
        chart: {
          type: 'pie',
          foreColor: '#333',
          background: 'transparent'
        },
        labels: this.filteredClientesProveedoresExpensesData.map(item => item.descripcion),
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        tooltip: {
          theme: 'light',
          y: {
            formatter: (value, { dataPointIndex }) => {
              // Get the corresponding item from filteredClientesProveedoresExpensesData
              const item = this.filteredClientesProveedoresExpensesData[dataPointIndex];
              if (item) {
                return `<div style="padding: 5px;">
                  <div style="font-weight: bold; margin-bottom: 5px;">${item.descripcion}</div>
                  <div style="margin-bottom: 3px;">Importe: ${this.formatCurrency(item.total)}</div>
                  <div>Cuenta: ${item.cuenta}</div>
                </div>`;
              }
              return this.formatCurrency(value);
            }
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        dataLabels: {
          style: {
            colors: ['#fff']
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    clientesProveedoresExpensesChartSeries() {
      return this.filteredClientesProveedoresExpensesData.map(item => item.total);
    },

    // Clientes Proveedores Income Bar Chart
    clientesProveedoresIncomeChartOptions() {
      return {
        chart: {
          type: 'bar',
          foreColor: '#333',
          background: 'transparent'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatCurrency(val, false),
          offsetX: 30,
          style: {
            colors: ['#333']
          }
        },
        xaxis: {
          categories: this.filteredClientesProveedoresIncomeData.map(item => item.descripcion),
          labels: {
            style: {
              colors: '#333'
            },
            formatter: (value) => this.formatCurrency(value, false)
          }
        },
        grid: {
          borderColor: '#ddd',
          row: {
            colors: ['#f8f9fa', '#ffffff']
          }
        },
        colors: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B'],
        tooltip: {
          theme: 'light',
          custom: ({ dataPointIndex }) => {
            const item = this.filteredClientesProveedoresIncomeData[dataPointIndex];
            return `
              <div class="apexcharts-tooltip-box">
                <div class="apexcharts-tooltip-title">${item.descripcion}</div>
                <div>Importe: ${this.formatCurrency(item.total)}</div>
                <div>Cuenta: ${item.cuenta}</div>
              </div>
            `;
          }
        },
        legend: {
          labels: {
            colors: '#333'
          }
        },
        title: {
          text: '',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'normal',
            color: '#666'
          }
        }
      };
    },

    clientesProveedoresIncomeChartSeries() {
      const data = this.filteredClientesProveedoresIncomeData.map(item => item.total);
      return [{
        name: 'Ingresos',
        data: data
      }];
    },

    // ======================
    // FILTROS AVANZADOS
    // ======================

    // Extraer ejercicios seleccionados del filtro
    selectedExercises() {
      if (this.dashboardFilters && this.dashboardFilters.ejercicio && this.dashboardFilters.ejercicio.value) {
        // Soporta múltiples ejercicios separados por comas: "2023,2024,2025"
        const value = this.dashboardFilters.ejercicio.value;
        if (value.includes(',')) {
          // Múltiples valores: split y convertir a números
          return value.split(',').map(v => parseInt(v.trim())).filter(v => !isNaN(v));
        } else {
          // Un solo valor
          return [parseInt(value)];
        }
      }
      return [];
    },

    // Extraer rango de meses del filtro
    selectedMonthRange() {
      const mesDesde = (this.dashboardFilters && this.dashboardFilters.mes_desde && this.dashboardFilters.mes_desde.value)
        ? parseInt(this.dashboardFilters.mes_desde.value)
        : null;
      const mesHasta = (this.dashboardFilters && this.dashboardFilters.mes_hasta && this.dashboardFilters.mes_hasta.value)
        ? parseInt(this.dashboardFilters.mes_hasta.value)
        : null;

      return {
        desde: mesDesde,
        hasta: mesHasta
      };
    },

    // Determinar si se debe usar filtros o selectores
    hasActiveFilters() {
      return this.selectedExercises.length > 0 ||
             this.selectedMonthRange.desde !== null ||
             this.selectedMonthRange.hasta !== null;
    },

    // Efectivo fiscal year a usar (filtro o selector)
    effectiveFiscalYear() {
      if (this.selectedExercises.length > 0) {
        // Si hay múltiples ejercicios, usar el primero
        return this.selectedExercises[0];
      }
      return this.selectedFiscalYear;
    },

    // Efectivo mes a usar (filtro o selector)
    effectiveMonth() {
      if (this.selectedMonthRange.desde !== null || this.selectedMonthRange.hasta !== null) {
        // Si hay filtro de mes, devolver 'todos' para que el hijo no filtre adicionalmente
        return 'todos';
      }
      return this.selectedMonth;
    },

    // Datos filtrados para DashboardGeneral
    filteredDashboardDataComputed() {
      // console.log('[DashboardKpis] filteredDashboardDataComputed called');
      if (!this.screen_data.dashboardData) return null;

      if (!this.hasActiveFilters) {
        // Sin filtros activos, devolver datos originales
        // console.log('[DashboardKpis] No active filters, returning original data');
        return this.screen_data.dashboardData;
      }

      // Aplicar filtros
      const filtered = {
        monthlyKpis: this.filterDataByExerciseAndMonth(this.screen_data.dashboardData.monthlyKpis),
        gastosIngresos: this.filterDataByExerciseAndMonth(this.screen_data.dashboardData.gastosIngresos),
        clientesProveedores: this.filterDataByExerciseAndMonth(this.screen_data.dashboardData.clientesProveedores),
        ultimasFacturas: this.screen_data.dashboardData.ultimasFacturas // Las facturas no se filtran por ahora
      };
      // console.log('[DashboardKpis] Applied filters, filtered data:', {
      //   monthlyKpis: filtered.monthlyKpis.length,
      //   gastosIngresos: filtered.gastosIngresos.length,
      //   clientesProveedores: filtered.clientesProveedores.length
      // });
      return filtered;
    },

    // Datos filtrados para DashboardPyG
    filteredPygDataComputed() {
      if (!this.screen_data.pygData || !Array.isArray(this.screen_data.pygData)) {
        return [];
      }

      if (!this.hasActiveFilters) {
        // Sin filtros activos, devolver datos originales
        return this.screen_data.pygData;
      }

      // Aplicar filtros
      return this.filterDataByExerciseAndMonth(this.screen_data.pygData);
    },

    // Filtros activos para mostrar en app-search-bar (chips visuales)
    activeFiltersForSearchBar() {
      const active = [];

      // Verificar si ejercicio tiene valor
      if (this.dashboardFilters && this.dashboardFilters.ejercicio && this.dashboardFilters.ejercicio.value) {
        active.push({ ...this.dashboardFilters.ejercicio, initial: true });
      }

      // Verificar si mes_desde tiene valor
      if (this.dashboardFilters && this.dashboardFilters.mes_desde && this.dashboardFilters.mes_desde.value) {
        active.push({ ...this.dashboardFilters.mes_desde, initial: true });
      }

      // Verificar si mes_hasta tiene valor
      if (this.dashboardFilters && this.dashboardFilters.mes_hasta && this.dashboardFilters.mes_hasta.value) {
        active.push({ ...this.dashboardFilters.mes_hasta, initial: true });
      }

      return active;
    },

    // Title of the dashboard that is updated with the language
    dashboardTitle() {
      return this.$t('App.dashboard');
    }
  },

  methods: {
    ...mapActions([
        'waitForLoggedIn',
    ]),
   ...mapActions({
      refreshUserData: "me/refreshUserData",
      refreshData: "documents/refreshData"
    }),
    ...mapActions('dashboard', {
      updateDashboardFilterAction: 'updateObject',
      resetDashboardFiltersAction: 'removeAllFilters',
      setAvailableEjercicios: 'setAvailableEjercicios'
    }),

    // Método auxiliar para filtrar datos por ejercicio y mes
    filterDataByExerciseAndMonth(data, mesEjercicioField = 'mes_ejercicio') {
      if (!data || !Array.isArray(data)) return [];

      return data.filter(item => {
        if (!item[mesEjercicioField]) return false;

        const [month, year] = item[mesEjercicioField].split('/');
        const itemMonth = parseInt(month);
        const itemYear = parseInt(year);

        // Filtrar por ejercicio
        if (this.selectedExercises.length > 0) {
          if (!this.selectedExercises.includes(itemYear)) {
            return false;
          }
        } else if (this.selectedFiscalYear) {
          if (itemYear !== this.selectedFiscalYear) {
            return false;
          }
        }

        // Filtrar por rango de meses
        if (this.selectedMonthRange.desde !== null) {
          if (itemMonth < this.selectedMonthRange.desde) return false;
        }
        if (this.selectedMonthRange.hasta !== null) {
          if (itemMonth > this.selectedMonthRange.hasta) return false;
        }

        // Si no hay filtro de mes pero sí selector de mes, aplicar la lógica antigua
        if (this.selectedMonthRange.desde === null && this.selectedMonthRange.hasta === null && this.selectedMonth !== 'todos') {
          return this.monthMatchesFilter(item[mesEjercicioField]);
        }

        return true;
      });
    },

    // Dashboard filter methods
    updateDashboardFilter(params) {
      // console.log('[DashboardKpis] updateDashboardFilter called with:', params);
      this.updateDashboardFilterAction(params);
      // console.log('[DashboardKpis] dashboardFilters after update:', this.dashboardFilters);
      // console.log('[DashboardKpis] hasActiveFilters:', this.hasActiveFilters);
      // console.log('[DashboardKpis] selectedExercises:', this.selectedExercises);
      // console.log('[DashboardKpis] selectedMonthRange:', this.selectedMonthRange);
      // Los datos se filtrarán automáticamente a través de computed properties
    },

    resetDashboardFilters() {
      this.resetDashboardFiltersAction();
      // Restaurar a los valores por defecto de los selectores
      if (this.availableFiscalYears && this.availableFiscalYears.length > 0) {
        this.selectedFiscalYear = this.availableFiscalYears[this.availableFiscalYears.length - 1];
      }
      this.selectedMonth = 'todos';
    },

    // Helper method to check if a month matches the selected month/quarter filter
    monthMatchesFilter(monthStr) {
      // If 'todos' is selected, return true for all months
      if (this.selectedMonth === 'todos') {
        return true;
      }
      
      // Check if monthStr is a valid string
      if (!monthStr || typeof monthStr !== 'string') {
        return false;
      }
      
      try {
        // Parse the month from the mes_ejercicio string (format: "month/year")
        const parts = monthStr.split('/');
        
        if (parts.length !== 2) {
          return false;
        }
        
        const month = parseInt(parts[0]);
        
        if (isNaN(month) || month < 1 || month > 12) {
          return false;
        }
        
        // Check for quarters
        if (this.selectedMonth === 'q1') {
          return month >= 1 && month <= 3;
        } else if (this.selectedMonth === 'q2') {
          return month >= 4 && month <= 6;
        } else if (this.selectedMonth === 'q3') {
          return month >= 7 && month <= 9;
        } else if (this.selectedMonth === 'q4') {
          return month >= 10 && month <= 12;
        }
        
        // Check for specific month
        const selectedMonth = parseInt(this.selectedMonth);
        return !isNaN(selectedMonth) && month === selectedMonth;
      } catch (error) {
        console.error('Error in monthMatchesFilter:', error, 'for monthStr:', monthStr);
        return false;
      }
    },
    retryLoadingData() {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        // console.log(`Retry attempt ${this.retryCount} of ${this.maxRetries} to load dashboard data`);
        
        setTimeout(async () => {
          if (this.currentCompany && this.currentCompany.id) {
            await this.loadDashboardData();
          } else {
            this.retryLoadingData();
          }
        }, this.retryInterval);
      } else {
        console.error('Dashboard: Failed to load data after maximum retry attempts');
        this.screen_data.isLoading = false;
      }
    },
    formatCurrency(value, includeSymbol = true) {
      // Handle null, undefined, NaN, or invalid values
      if (value === null || value === undefined || isNaN(value) || !Number.isFinite(Number(value))) {
        return includeSymbol ? '0,00 €' : '0,00';
      }
      
      try {
        const formatter = new Intl.NumberFormat('es-ES', {
          style: includeSymbol ? 'currency' : 'decimal',
          currency: 'EUR',
          minimumFractionDigits: 2
        });
        return formatter.format(value);
      } catch (error) {
        console.error('Error formatting currency value:', value, error);
        return includeSymbol ? '0,00 €' : '0,00';
      }
    },

    getMonthName(month) {
      const months = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ];
      return months[month - 1];
    },
    
    async loadDashboardData() {
      let repo;
      this.selectedFiscalYear = null;
      this.selectedMonth = 'todos';
      if (this.user_data_id && this.currentCompany && this.currentCompany.id) {
        this.screen_data.isLoading = true;

        try {
          await this.refreshData();
        } catch (error) {
          console.error('Error loading documents into store:', error);
        }

        repo = new ApiDashboardRepository(query_dispatcher_2, this.user_data_id, this.currentCompany.id);
        // repo = new MemoryDashboardRepository(query_dispatcher_2, this.user_data_id, this.currentCompany.id);
      }

      if (repo) {
        const loader = new LoadDashboard(repo, this.screen_data);
        const pygLoader = new LoadPyGData(repo);

        try {
          // Load both dashboard general data and P&G data in parallel
          const [, pygResult] = await Promise.all([
            loader.load(),
            pygLoader.load()
          ]);

          // Store P&G data
          this.screen_data.pygGrupos = pygResult.pygGrupos;
          this.screen_data.pygData = pygResult.pygData;

          // Set default fiscal year if available
          if (this.availableFiscalYears && this.availableFiscalYears.length > 0) {
            // Populate filter with available ejercicios from real data
            this.setAvailableEjercicios(this.availableFiscalYears);

            // Set most recent year as default
            this.selectedFiscalYear = this.availableFiscalYears[this.availableFiscalYears.length - 1];
          }

          // Force update to ensure charts are rendered
          this.$forceUpdate();
        } catch (error) {
          console.error('Error loading dashboard data:', error);
        } finally {
          // Set loading state to false after data is loaded (or if there was an error)
          this.screen_data.isLoading = false;
        }
      }
    },
    
    selectRow(item) {
      // Navigate to document detail page with the document ID
      this.$router.push({
        name: "document-detail",
        params: { document: item.id }
      });
    },
    
    getTipoColor(tipo) {
      switch (tipo.toLowerCase()) {
        case 'emitida':
          return 'green'; // Color for Client invoices
        case 'recibida':
          return 'blue';  // Color for Supplier invoices
        case 'cliente':
          return 'purple'; // Additional color for 'cliente' if it exists
        case 'proveedor':
          return 'orange'; // Additional color for 'proveedor' if it exists
        default:
          return 'grey';  // Default color for unknown types
      }
    },

    create_dashboard_op() {
      if (!this.hasDashboardData){
        this.buttonLoading = true; // Set button loading state
      }else{
        this.screen_data.isLoading = true;
      }
      this.dashboardOperationState = 'pending';
      
      client2.put('create_operation_no_staff/', { 
        'params': { 
          'name': 'GetDashboardData', 
          'channel': 'rest', 
          'companydb': this.currentCompany.id 
        }
      }).then((response) => {
        // Check for the expected response format: {'ok':'ok', 'operation_id': operation.id}
        if (response.data && response.data.operation_id) {
          this.dashboardOperationId = response.data.operation_id;
          this.intervalDashboard = setInterval(this.fetch_dashboard_operation_state, 2000);
        } else {
          console.error('No operation ID returned from dashboard operation');
          this.dashboardOperationState = 'error';
          this.screen_data.isLoading = false;
          this.buttonLoading = false; // Reset button loading state
        }
      }).catch(err => {
        console.error('Error creating dashboard operation:', err);
        this.dashboardOperationState = 'error';
        this.screen_data.isLoading = false;
        this.buttonLoading = false; // Reset button loading state
      });
    },
    
    fetch_dashboard_operation_state() {
      try {
        client2.get(`operations/${this.dashboardOperationId}/`).then(response => {
          this.dashboardOperationState = response.data.state;
          
          if (this.dashboardOperationState === "done") {
            clearInterval(this.intervalDashboard);
            this.screen_data.isLoading = false;
            this.buttonLoading = false; // Reset button loading state
            this.loadDashboardData(); // Reload dashboard data when operation completes
          } else if (this.dashboardOperationState === "error") {
            clearInterval(this.intervalDashboard);
            this.screen_data.isLoading = false;
            this.buttonLoading = false; // Reset button loading state
            console.error('Dashboard operation failed');
          }
        }).catch(error => {
          console.error('Error fetching dashboard operation state:', error);
          clearInterval(this.intervalDashboard);
          this.dashboardOperationState = 'error';
          this.screen_data.isLoading = false;
          this.buttonLoading = false; // Reset button loading state
        });
      } catch (error) {
        console.error('Exception in fetch_dashboard_operation_state:', error);
        clearInterval(this.intervalDashboard);
        this.dashboardOperationState = 'error';
        this.screen_data.isLoading = false;
        this.buttonLoading = false; // Reset button loading state
      }
    },
    
    
  },

  async mounted() {
    // Wait for user to be logged in and store to be initialized
    await this.waitForLoggedIn();
    await this.refreshUserData();
    
    // Check if currentCompany is available
    if (this.currentCompany && this.currentCompany.id) {
      await this.loadDashboardData();
    } else {
      // console.warn('Dashboard: currentCompany not available, will retry loading data');
      this.retryLoadingData();
    }
  },
  
  beforeDestroy() {
    // Clean up interval if it's still active
    if (this.intervalDashboard) {
      clearInterval(this.intervalDashboard);
    }
  },
  
  watch: {
    currentCompany: {
      handler: async function(newCompany, oldCompany) {
        if (newCompany && oldCompany && newCompany.id !== oldCompany.id) {
          await this.loadDashboardData();
        }
      },
      deep: true
    },
    
    // Watch for dashboard data changes to set default selected years and check for data
    'screen_data.dashboardData': {
      handler: function(newData) {
        // Only proceed with setting fiscal years if we have data
        if (newData && newData.gastosIngresos) {
          // Set default global fiscal year to the most recent year
          if (this.availableFiscalYears.length > 0 && this.selectedFiscalYear === null) {
            this.selectedFiscalYear = this.availableFiscalYears[0]; // Most recent year (already sorted)
          }
        }
      },
      immediate: true
    },
    
    
    // Watch for changes in selectedMonth
    selectedMonth: {
      handler: function() {
        // Force recalculation of filteredTotalKpis by accessing it
        this.filteredTotalKpis;
      }
    },
    
    // Watch for changes in selectedFiscalYear
    selectedFiscalYear: {
      handler: function() {
        // Force recalculation of filteredTotalKpis by accessing it
        this.filteredTotalKpis;
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  padding: 20px;
}

h1 {
  font-size: 2em;
  font-weight: 300;
  margin-bottom: 30px;
  color: #333;
}

.dashboard-kpis-fallback-card {
  border-radius: 12px;
  -webkit-box-shadow: none;
  box-shadow: none;
  overflow: visible;
  height: auto;
  padding-bottom: 20px;
  background-color: var(--v-neutral-base);
  margin-bottom: 30px;
}

.dashboard-kpis-fallback-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 20px;
  font-weight: 500;
  border-bottom: none;
  padding-bottom: 10px;
}

.kpi-card {
  transition: transform 0.3s;
  height: 100%;
}

.kpi-card:hover {
  transform: translateY(-5px);
}

.kpi-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin-bottom: 15px;
}

.kpi-title {
  font-size: 1.1em;
  color: #666;
  margin-bottom: 10px;
}

.kpi-value {
  font-size: 1.8em;
  font-weight: 500;
  color: #333;
}

/* Chart styles to ensure equal heights */
.chart-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 400px;
  max-height: 450px;
  padding: 0;
  overflow: hidden;
}

.chart-element {
  width: 100%;
  height: 350px;
  max-height: 350px;
  overflow: visible;
}

/* Year selector dropdown styling */
.year-selector {
  max-width: 150px;
  margin-left: 10px;
}

/* Make the dropdown text more visible */
.v-select.year-selector .v-select__selection,
.v-select.global-year-selector .v-select__selection {
  font-weight: 500;
  color: #333;
}

/* Global year selector styling */
.global-year-selector {
  max-width: 180px;
  margin-left: 16px;
}

/* Global month selector styling */
.global-month-selector {
  max-width: 180px;
}

/* Style for the fiscal year indicator in card titles */
.subtitle-1.ml-2 {
  color: #666;
  font-weight: normal;
}

/* Inner dashboard card styling */
.inner-dashboard-card {
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  background-color: #ffffff;
}

.inner-dashboard-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 18px;
  font-weight: 500;
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 15px;
}

/* Add padding to the main card's content area */
.dashboard-kpis-fallback-card > .v-card__text {
  padding: 20px;
}

.dashboard-kpis-fallback-card {
  height: auto;
  min-height: 480px;
  border-radius: 12px;
  -webkit-box-shadow: none;
  box-shadow: none;
  overflow: visible;
}
.dashboard-kpis-fallback-card .v-card__title {
  color: var(--v-primary-base);
  font-size: 20px;
  font-weight: 500;
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 30px;
}

.dashboard-kpis-fallback-card .centered-content {
  text-align: center;
  width: 98%;
  margin: 0;
  position: absolute;
  top: 50%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
}

/* No data message styling */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 350px;
  padding: 20px;
  text-align: center;
  background-color: var(--v-neutral-base);
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
}

.no-data-message .v-icon {
  margin-bottom: 16px;
  opacity: 0.7;
}

.no-data-text {
  color: #757575;
  font-size: 16px;
  font-weight: 400;
  max-width: 80%;
  margin: 0 auto;
}

.warning-text {
  font-size: 13px !important;
  width: 100%;
  margin: 0 !important;
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  border-top: 1px solid #e6e6e6;
}

/* Tabs styling */
.dashboard-tabs {
  border-bottom: 1px solid #e6e6e6;
  margin-top: 0;
}

.dashboard-tabs .v-tab {
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
}

.dashboard-tabs .v-tab--active {
  color: var(--v-primary-base);
}

</style>